import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import { getArticles } from "../../../services/articlesServices";

import { FaShoppingCart, FaRegCreditCard } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

import ArticleCard from "./ArticleCard";
import CartModal from "./CartModal";

function normalizeArticle(article) {
  return {
    ...article,
    is_stockable: Boolean(article?.is_stockable ?? article?.is_stockage ?? false),
    quantite_stock: Number(article?.quantite_stock ?? 0),
    prix: Number(article?.prix ?? 0),
    taille: article?.taille || "",
    categorie: article?.categorie || "",
  };
}

export default function Boutique() {
  useScrollToTop();
  const navigate = useNavigate();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [articles, setArticles] = useState([]);
  const [filtreCategorie, setFiltreCategorie] = useState("Toutes les catégories");
  const [categories, setCategories] = useState(["Toutes les catégories"]);

  const [cart, setCart] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchArticles = useCallback(async () => {
    try {
      const articlesData = await getArticles();

      const normalized = (articlesData || []).map(normalizeArticle);
      const active = normalized.filter((a) => a.is_active);

      setArticles(active);

      const cats = Array.from(
        new Set(active.map((a) => a.categorie).filter(Boolean))
      );
      setCategories(["Toutes les catégories", ...cats]);
    } catch (e) {
      console.error("Erreur getArticles :", e);
      toast.error("Erreur lors de la récupération des articles.");
    }
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  const addToCart = (article) => {
    const normalizedArticle = normalizeArticle(article);

    setCart((prev) => {
      const found = prev.find((i) => i.id === normalizedArticle.id);

      if (found) {
        if (
          normalizedArticle.is_stockable &&
          found.quantity + 1 > Number(normalizedArticle.quantite_stock ?? 0)
        ) {
          toast.warn(`Stock insuffisant pour "${normalizedArticle.nom}"`);
          return prev;
        }

        return prev.map((i) =>
          i.id === normalizedArticle.id
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }

      return [
        ...prev,
        {
          ...normalizedArticle,
          quantity: 1,
          message: "",
        },
      ];
    });

    toast.success(`"${normalizedArticle.nom}" ajouté au panier.`);
  };

  const removeFromCart = (id) => {
    setCart((prev) => prev.filter((i) => i.id !== id));
    toast.success("Article retiré du panier.");
  };

  const toggleModal = () => setIsModalOpen((v) => !v);

  const filteredArticles = useMemo(
    () =>
      articles.filter(
        (a) =>
          filtreCategorie === "Toutes les catégories" ||
          a.categorie === filtreCategorie
      ),
    [articles, filtreCategorie]
  );

  const cartUnits = useMemo(
    () => cart.reduce((n, it) => n + (Number(it.quantity) || 0), 0),
    [cart]
  );

  const handlePayement = () => {
    if (!user) {
      toast.error("Veuillez vous connecter pour accéder à cette page.");
      navigate("/login");
    } else {
      navigate("/commandes-&-reglements");
    }
  };

  return (
    <section className="w-full">
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Utilisateur"
          title="Boutique"
          align="center"
          className="mb-1"
        />

        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h4 className="text-lg sm:text-xl font-semibold">
                  Boutique du club
                </h4>
                <p className="text-white/90 text-sm">
                  Sélectionnez vos articles, validez le panier puis réglez dans
                  « Commandes & Règlements ».
                </p>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Button
                    text="Afficher mon Panier"
                    onClick={toggleModal}
                    icon={FaShoppingCart}
                    className="!rounded-xl !bg-white/15 !text-white hover:!bg-white/25 ring-1 ring-white/30 gap-2"
                    hoverLabel="Voir le panier"
                  />

                  {cartUnits > 0 && (
                    <span
                      className="absolute -top-2 -right-2 min-w-[1.6rem] h-6 px-2 rounded-full bg-white text-slate-900 text-xs font-bold grid place-items-center shadow"
                      aria-label={`${cartUnits} unité(s) dans le panier`}
                      title={`${cartUnits} unité(s)`}
                    >
                      {cartUnits}
                    </span>
                  )}
                </div>

                <Button
                  text="Commandes & Règlements"
                  onClick={handlePayement}
                  icon={FaRegCreditCard}
                  className="!rounded-xl !bg-white/15 !text-white hover:!bg-white/25 ring-1 ring-white/30 gap-2"
                />
              </div>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Filtrer par catégorie
              </label>
              <select
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                onChange={(e) => setFiltreCategorie(e.target.value)}
                value={filtreCategorie}
              >
                {categories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6 text-sm text-slate-700 dark:text-slate-300 space-y-1">
              <p>
                <span className="font-semibold">1/</span> Ajoutez vos articles au
                panier.
              </p>
              <p>
                <span className="font-semibold">2/</span> Ouvrez le panier,
                ajustez quantités / tailles, puis « Passer la commande ».
              </p>
              <p>
                <span className="font-semibold">3/</span> Réglez votre commande
                dans « Commandes & Règlements ».
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((a) => (
                <ArticleCard
                  key={a.id}
                  id={a.id}
                  nom={a.nom}
                  description={a.description}
                  prix={a.prix}
                  photo_url={a.photo_url}
                  is_stockable={a.is_stockable}
                  is_stockage={a.is_stockage}
                  quantite_stock={a.quantite_stock}
                  taille={a.taille}
                  categorie={a.categorie}
                  is_active={a.is_active}
                  ordre_article={a.ordre_article}
                  addToCart={() => addToCart(a)}
                />
              ))}

              {filteredArticles.length === 0 && (
                <div className="col-span-full text-center py-12 text-slate-600 dark:text-slate-300">
                  Aucun article dans cette catégorie pour le moment.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <CartModal
          cart={cart}
          setCart={setCart}
          closeModal={toggleModal}
          removeFromCart={removeFromCart}
          onOrderSuccess={fetchArticles}
        />
      )}

      <ToastContainer />
    </section>
  );
}